import { useRef, useState } from 'react';
import type { ChangeEvent } from 'react';

import { MenuIcon } from '@/components/MenuIcon';
import { shrinkImage } from '@/lib/image';
import { usePhotos, useUploadPhoto, type PhotoTarget } from '@/lib/queries';
import type { Uuid } from '@/lib/types';
import { usePhotoUrl } from '@/lib/usePhotoUrl';

/**
 * 사진 격자 + 카메라 버튼 — 검측·작업일보·부적합이 같은 것을 쓴다(DOC-84 §3-D-3 · D-4).
 *
 * <p>사진은 {@code <img src>} 로 바로 붙일 수 없다 — Bearer 토큰이라 브라우저가 이미지 요청에
 * 인증을 싣지 않는다. 한 장씩 {@code apiFetchBlob} 으로 받아 오브젝트 URL 을 만든다.
 *
 * <p>{@code capture="environment"} — 휴대폰에서 누르면 뒤 카메라가 바로 열린다. 갤러리에서 고르는
 * 길도 같은 입력이 연다(브라우저가 둘 다 묻는다). 올리기 전에 긴 변 2,000px 로 줄인다.
 *
 * @param canUpload 누가 붙일 수 있는지는 화면이 정한다 — 협력사이고 그 건이 열려 있을 때
 */
export function PhotoGrid({
  target,
  parentId,
  canUpload,
  onUploaded,
}: {
  target: PhotoTarget;
  parentId: Uuid;
  canUpload: boolean;
  /** 올린 뒤 부모 목록(사진 수)을 새로 읽어야 하면 화면이 넘긴다. */
  onUploaded?: () => void;
}) {
  const photos = usePhotos(target, parentId);
  const upload = useUploadPhoto(target, parentId);
  const input = useRef<HTMLInputElement>(null);
  const [failed, setFailed] = useState(false);
  const rows = photos.data ?? [];

  async function onPick(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length === 0) return;
    setFailed(false);
    try {
      // 한 장씩 순서대로 — 동시에 보내면 현장 LTE 에서 전부 늦어진다.
      for (const file of files) {
        await upload.mutateAsync(await shrinkImage(file));
      }
      onUploaded?.();
    }
    catch {
      setFailed(true);
    }
  }

  if (photos.isPending) {
    return <p className="py-2 text-ui-xs" style={{ color: 'var(--text-muted)' }}>불러오는 중입니다…</p>;
  }

  return (
    <div>
      {(rows.length > 0 || canUpload) && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {rows.map((photo) => (
            <Thumb key={photo.id} path={`/${target}/${parentId}/photos/${photo.id}`} alt={photo.fileName} />
          ))}
          {canUpload && (
            <button
              type="button"
              className="flex aspect-square items-center justify-center rounded-md border border-dashed disabled:opacity-50"
              style={{ borderColor: 'var(--border-strong)', color: 'var(--text-muted)' }}
              onClick={() => input.current?.click()}
              disabled={upload.isPending}
              aria-label="사진 추가"
            >
              <MenuIcon name={upload.isPending ? 'clipboard' : 'camera'} size={22} />
            </button>
          )}
        </div>
      )}
      {canUpload && (
        <input
          ref={input}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          className="hidden"
          onChange={onPick}
        />
      )}
      {failed && (
        <p className="mt-2 text-ui-xs" style={{ color: 'var(--danger)' }}>사진을 올리지 못했습니다.</p>
      )}
    </div>
  );
}

/** 한 장. 바이트가 오기 전에는 빈 칸 — 깨진 이미지 아이콘을 잠깐이라도 보이지 않는다. */
function Thumb({ path, alt }: { path: string; alt: string }) {
  const url = usePhotoUrl(path);
  return (
    <div className="aspect-square overflow-hidden rounded-md" style={{ background: 'var(--bg-hover)' }}>
      {url !== null && <img src={url} alt={alt} className="h-full w-full object-cover" loading="lazy" />}
    </div>
  );
}
